import { Loader2, Phone, Search } from "lucide-react";
import { useState } from "react";
import { useGetList, useNotify, useRefresh } from "ra-core";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

import { SectionCard } from "../data-display/SectionCard";
import { ListPageHeader } from "../list/ListPageHeader";
import { unwrapFunctionMessage } from "../lib/functionErrors";
import { getSupabaseClient } from "../../atomic-crm/providers/supabase/supabase";
import { getActiveWorkspaceId } from "../workspace/activeWorkspace";

interface AvailableNumber {
  e164: string;
  friendly: string;
  locality: string | null;
  region: string | null;
}

interface OwnedNumber {
  id: string;
  e164: string;
  kind: string;
  status: string;
  billing_product_key: string;
}

const inputClass =
  "h-9 rounded-md border border-input bg-transparent px-2.5 text-[13px] outline-none focus-visible:ring-2 focus-visible:ring-ring";

/**
 * Gravun Business Phone (/#/phone): pick and buy a number without ever
 * seeing Twilio.
 *
 * The search is free; the ONLY billable action is the purchase, and it
 * requires this page's explicit confirmation step naming the exact number
 * and its monthly price. Behind the scenes each workspace gets an isolated
 * Twilio subaccount, and the add-on lands on the Gravun subscription as a
 * PENDING item that only the Stripe webhook activates.
 */
export const PhonePage = () => {
  const notify = useNotify();
  const refresh = useRefresh();
  const workspaceId = getActiveWorkspaceId();

  const [country, setCountry] = useState("US");
  const [areaCode, setAreaCode] = useState("");
  const [kind, setKind] = useState<"local" | "tollfree">("local");
  const [results, setResults] = useState<AvailableNumber[] | null>(null);
  const [confirming, setConfirming] = useState<AvailableNumber | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  const { data: numbers } = useGetList<OwnedNumber>("phone_numbers", {
    pagination: { page: 1, perPage: 50 },
    sort: { field: "created_at", order: "DESC" },
  });
  const owned = (numbers ?? []).filter((n) => n.status !== "released");

  const priceLabel = (target: "local" | "tollfree") =>
    owned.length === 0 && target === "local"
      ? "$19/month (Gravun Business Phone, includes this number)"
      : target === "tollfree"
        ? "$15/month"
        : "$10/month";

  const call = async (body: Record<string, unknown>) => {
    const { data, error } = await getSupabaseClient().functions.invoke(
      "phone-service",
      { body: { ...body, workspace_id: workspaceId } },
    );
    if (error) {
      const context = (error as { context?: Response }).context;
      let message = error.message;
      if (context && typeof context.json === "function") {
        try {
          message = unwrapFunctionMessage(await context.json()) ?? message;
        } catch {
          // keep generic
        }
      }
      throw new Error(message);
    }
    return data as Record<string, unknown>;
  };

  const search = async () => {
    setBusy("search");
    setResults(null);
    setConfirming(null);
    try {
      const data = await call({
        action: "search_numbers",
        country,
        area_code: areaCode || undefined,
        kind,
      });
      setResults((data.numbers as AvailableNumber[]) ?? []);
    } catch (caught) {
      notify(caught instanceof Error ? caught.message : "Search failed", {
        type: "warning",
      });
    } finally {
      setBusy(null);
    }
  };

  const purchase = async (number: AvailableNumber) => {
    setBusy(number.e164);
    try {
      await call({
        action: "purchase_number",
        e164: number.e164,
        kind,
        country,
      });
      notify(`${number.e164} is yours. Calls and texts land in your inbox.`);
      setResults(null);
      setConfirming(null);
      refresh();
    } catch (caught) {
      notify(caught instanceof Error ? caught.message : "Purchase failed", {
        type: "warning",
      });
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="flex flex-col gap-4">
      <ListPageHeader
        title="Phone & SMS"
        subtitle="Business numbers managed by Gravun; no Twilio account needed"
      />

      <SectionCard title="Your numbers">
        {owned.length === 0 ? (
          <p className="px-1 text-[12.5px] text-muted-foreground">
            No business number yet. Your first local number comes with Gravun
            Business Phone at $19/month, calls, SMS, voicemail and inbox
            integration included.
          </p>
        ) : (
          <div className="flex flex-col px-1">
            {owned.map((number) => (
              <div
                key={number.id}
                className="flex items-center gap-3 border-b border-border-subtle py-2 last:border-0 text-[13.5px]"
              >
                <Phone className="size-4 text-muted-foreground" />
                <span className="font-medium tabular-nums">{number.e164}</span>
                <span className="rounded bg-muted px-1.5 py-0.5 text-[11px] capitalize">
                  {number.kind}
                </span>
                <span
                  className={cn(
                    "ml-auto text-[12px]",
                    number.status === "active"
                      ? "text-emerald-600"
                      : "text-muted-foreground",
                  )}
                >
                  {number.status}
                </span>
              </div>
            ))}
          </div>
        )}
      </SectionCard>

      <SectionCard
        title={
          owned.length === 0
            ? "Get a Gravun Business Number"
            : "Add another number"
        }
      >
        <div className="flex flex-col gap-3 px-1">
          <div className="flex flex-wrap items-end gap-2">
            <label className="flex flex-col gap-1 text-[12px] font-medium">
              Country
              <select
                className={cn(inputClass, "w-28")}
                value={country}
                onChange={(e) => setCountry(e.target.value)}
              >
                <option value="US">United States</option>
                <option value="CA">Canada</option>
              </select>
            </label>
            <label className="flex flex-col gap-1 text-[12px] font-medium">
              Type
              <select
                className={cn(inputClass, "w-32")}
                value={kind}
                onChange={(e) =>
                  setKind(e.target.value as "local" | "tollfree")
                }
              >
                <option value="local">Local</option>
                <option value="tollfree">Toll-free</option>
              </select>
            </label>
            {kind === "local" ? (
              <label className="flex flex-col gap-1 text-[12px] font-medium">
                Area code
                <input
                  className={cn(inputClass, "w-24")}
                  placeholder="416"
                  maxLength={3}
                  value={areaCode}
                  onChange={(e) =>
                    setAreaCode(e.target.value.replace(/\D/g, ""))
                  }
                />
              </label>
            ) : null}
            <Button
              size="sm"
              disabled={busy === "search"}
              onClick={() => void search()}
            >
              {busy === "search" ? (
                <Loader2 className="mr-1 size-3.5 animate-spin" />
              ) : (
                <Search className="mr-1 size-3.5" />
              )}
              Find numbers
            </Button>
          </div>
          <p className="text-[11.5px] text-muted-foreground">
            {priceLabel(kind)}. Searching is free; nothing is billed until you
            confirm a specific number.
          </p>

          {results !== null ? (
            results.length === 0 ? (
              <p className="text-[12.5px] text-muted-foreground">
                No numbers matched; try another area code.
              </p>
            ) : (
              <div className="flex flex-col gap-1">
                {results.map((number) => (
                  <div
                    key={number.e164}
                    className="flex flex-wrap items-center gap-2 rounded-md border border-border-subtle px-2 py-1.5 text-[13px]"
                  >
                    <span className="font-medium tabular-nums">
                      {number.friendly || number.e164}
                    </span>
                    {number.locality ? (
                      <span className="text-[11.5px] text-muted-foreground">
                        {number.locality}
                        {number.region ? `, ${number.region}` : ""}
                      </span>
                    ) : null}
                    {confirming?.e164 === number.e164 ? (
                      <span className="ml-auto flex items-center gap-2">
                        <span className="text-[12px]">
                          Buy {number.e164} at {priceLabel(kind)}?
                        </span>
                        <Button
                          size="sm"
                          disabled={busy === number.e164}
                          onClick={() => void purchase(number)}
                        >
                          {busy === number.e164 ? (
                            <Loader2 className="size-3.5 animate-spin" />
                          ) : (
                            "Confirm purchase"
                          )}
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => setConfirming(null)}
                        >
                          Cancel
                        </Button>
                      </span>
                    ) : (
                      <Button
                        size="sm"
                        variant="outline"
                        className="ml-auto"
                        onClick={() => setConfirming(number)}
                      >
                        Choose
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            )
          ) : null}
        </div>
      </SectionCard>

      <SectionCard title="Coming soon">
        <p className="px-1 text-[12.5px] text-muted-foreground">
          Port your existing business number into Gravun, and bring-your-own
          Twilio for advanced setups.
        </p>
      </SectionCard>
    </div>
  );
};
